/**
 * A Set-like data structure that serializes keys before storage.
 * Useful when keys are complex objects that need to be compared by value rather than reference.
 */
export class SerializedKeySet<T> {
  private map: Map<string, T> = new Map();
  private keySerializer: (key: T) => string;

  constructor(keySerializer: (key: T) => string);
  constructor(keySerializer: (key: T) => string, iterable: Iterable<T>);
  constructor(other: SerializedKeySet<T>);

  constructor(keySerializerOrOther: ((key: T) => string) | SerializedKeySet<T>, iterable?: Iterable<T>) {
    if (typeof keySerializerOrOther === "function") {
      this.keySerializer = keySerializerOrOther;
      if (iterable) {
        for (const key of iterable) {
          this.add(key);
        }
      }
    } else {
      this.keySerializer = keySerializerOrOther.keySerializer;
      for (const key of keySerializerOrOther.keys()) {
        this.add(key);
      }
    }
  }

  add(key: T): this {
    const serializedKey = this.keySerializer(key);
    this.map.set(serializedKey, key);
    return this;
  }

  has(key: T): boolean {
    const serializedKey = this.keySerializer(key);
    return this.map.has(serializedKey);
  }

  delete(key: T): boolean {
    const serializedKey = this.keySerializer(key);
    return this.map.delete(serializedKey);
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }

  *keys(): SetIterator<T> {
    yield* this.values();
  }

  *values(): SetIterator<T> {
    for (const entry of this.map.values()) {
      yield entry;
    }
  }

  *entries(): SetIterator<[T, T]> {
    for (const key of this.keys()) {
      yield [key, key];
    }
  }

  [Symbol.iterator](): SetIterator<T> {
    return this.keys();
  }

  forEach(callback: (value: T, value2: T, set: SerializedKeySet<T>) => void): void {
    for (const key of this.keys()) {
      callback(key, key, this);
    }
  }
}
